import React, { useEffect } from 'react';
import { IconButton } from '@mui/material';
import { colors } from '@roku-web-ui/rds-theme';

import { IconSirenOff, IconSirenOn } from 'components/icons';
import { Tooltip } from 'components/common/Tooltip';

import { useAspenLogger } from 'hooks/useAspenLogger';
import { useAspenSocket } from 'hooks/useAspenSocket';
import { useRemainingTimer } from 'hooks/useRemainingTimer';

import { createCommand } from 'modules/aspenCommands';
import { formatDuration } from 'modules/time';

import { AppLeaf } from 'types/common/leaf';
import { CameraState } from 'types/aspen/state';

interface SirenProps {
  leaf: AppLeaf;
}

export const Siren = ({ leaf }: SirenProps) => {
  const { logAspenEvent } = useAspenLogger();

  const sirenState = (leaf?.state as CameraState)?.siren;
  const { sendCommand } = useAspenSocket();
  const { remainingTime, setExpirationTime } = useRemainingTimer({ expiration: sirenState?.expiration || 0 });

  const isSirenOn = sirenState?.siren === 'on' && (remainingTime > 0 || !sirenState?.expiration);

  useEffect(() => {
    if (sirenState?.expiration) {
      setExpirationTime(sirenState?.expiration);
    }
  }, [sirenState?.expiration]);

  return (
    <Tooltip
      title={`Siren${isSirenOn && remainingTime > 0 ? ` (${formatDuration(remainingTime)})` : ''}`}
      placement="top"
    >
      <IconButton
        aria-label="siren"
        data-testid="button-siren"
        onClick={() => {
          sendCommand({
            leaf,
            command: createCommand('siren', sirenState?.siren === 'on' ? 'off' : 'on'),
          });

          logAspenEvent({
            log_id: 'iot.web.stream.interaction',
            payload: {
              device_id: leaf?.id,
              interaction: 'siren',
            },
          });
        }}
        sx={{
          '&:hover': {
            boxShadow: 'none',
          },
        }}
      >
        {isSirenOn ? <IconSirenOn sx={{ color: '#FF3535' }} /> : <IconSirenOff sx={{ color: colors.grey[400] }} />}
      </IconButton>
    </Tooltip>
  );
};
